import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QRCode } from "@/components/ui/kibo-ui/qr-code";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Copy, Check, Shield } from "lucide-react";
import { authClient } from "@/utils/auth/client";
import { useMutation, useQuery } from "@tanstack/react-query";

interface TotpSetupFormProps {
    password: string;
    onSetupComplete: (backupCodes: string[]) => void;
    onCancel: () => void;
}

export const TotpSetupForm: React.FC<TotpSetupFormProps> = ({ password, onSetupComplete, onCancel }) => {
    const [verificationCode, setVerificationCode] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copiedSecret, setCopiedSecret] = useState(false);

    // Get TOTP URI for QR code
    const { data: totpData, isLoading } = useQuery({
        queryKey: ["totp-enable", password],
        queryFn: async () => {
            const result = await authClient.twoFactor.enable({ password });
            if (result.error) {
                throw new Error(result.error.message || "Failed to enable 2FA");
            }
            return result.data;
        },
        enabled: !!password,
    });

    // Since enable() already returned the backup codes, we just need to verify
    // and then use the backup codes from totpData

    // Verify TOTP code mutation
    const verifyTotpMutation = useMutation({
        mutationFn: async (code: string) => {
            const result = await authClient.twoFactor.verifyTotp({
                code,
            });
            if (result.error) {
                throw new Error(result.error.message || "Invalid verification code");
            }
            return result.data;
        },
        onSuccess: () => {
            // After successful verification, 2FA is enabled and we have backup codes
            if (totpData?.backupCodes) {
                onSetupComplete(totpData.backupCodes);
            }
        },
        onError: (error: any) => {
            setError(error.message || "Invalid verification code");
        },
    });

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verificationCode.trim()) {
            setError("Please enter the verification code");
            return;
        }

        setError(null);
        setIsVerifying(true);

        verifyTotpMutation.mutate(verificationCode, {
            onSettled: () => {
                setIsVerifying(false);
            },
        });
    };

    const copySecret = async () => {
        if (totpData?.totpURI) {
            // Extract secret from URI for manual entry
            const secretMatch = totpData.totpURI.match(/secret=([^&]+)/);
            if (secretMatch) {
                await navigator.clipboard.writeText(secretMatch[1]);
                setCopiedSecret(true);
                setTimeout(() => setCopiedSecret(false), 2000);
            }
        }
    };

    if (isLoading) {
        return (
            <Card className="mx-auto w-full max-w-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Setting up 2FA...
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="mx-auto w-full max-w-md">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Set up Two-Factor Authentication
                </CardTitle>
                <CardDescription>
                    Scan the QR code with your authenticator app or enter the secret manually
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {totpData?.totpURI && (
                    <div className="space-y-4">
                        {/* QR Code */}
                        <div className="flex justify-center rounded-lg bg-white p-4">
                            <QRCode data={totpData.totpURI} className="h-48 w-48" />
                        </div>

                        {/* Manual entry option */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Can't scan? Enter this code manually:</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    value={totpData.totpURI.match(/secret=([^&]+)/)?.[1] || ""}
                                    readOnly
                                    className="font-mono text-sm"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={copySecret}
                                    className="shrink-0"
                                >
                                    {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>

                        {/* Verification form */}
                        <form onSubmit={handleVerify} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="verification-code">
                                    Enter the 6-digit code from your authenticator app:
                                </Label>
                                <Input
                                    id="verification-code"
                                    type="text"
                                    placeholder="123456"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    maxLength={6}
                                    className="text-center text-lg tracking-widest"
                                />
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onCancel}
                                    disabled={isVerifying}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isVerifying || verificationCode.length !== 6}
                                    className="flex-1"
                                >
                                    {isVerifying ? (
                                        <div className="flex items-center gap-2">
                                            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                            Verifying...
                                        </div>
                                    ) : (
                                        "Verify & Enable"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
